// Asset Management Types

import type { AlertSummary } from './alerts';

export type AssetStatus = 
  | 'inService'        // Asset is currently in use
  | 'outOfService'     // Temporarily not in use but still owned
  | 'disposed'         // Asset has been sold, scrapped, or written off
  | 'fullyDepreciated' // Asset has reached the end of its useful life for accounting purposes but may still be in use
  | 'pending'          // Asset has been purchased but not yet received or commissioned
  | 'underMaintenance' // Temporarily out of service for repairs
  | 'reserved'         // Allocated for a specific purpose or project
  | 'lostMissing'      // Cannot be located
  | 'leased';          // Asset is leased rather than owned outright

// Status display labels for UI
export const ASSET_STATUS_LABELS: Record<AssetStatus, string> = {
  inService: 'In Service',
  outOfService: 'Out of Service',
  disposed: 'Disposed',
  fullyDepreciated: 'Fully Depreciated',
  pending: 'Pending / On Order',
  underMaintenance: 'Under Maintenance / Repair',
  reserved: 'Reserved',
  lostMissing: 'Lost / Missing',
  leased: 'Leased',
};

// All available asset statuses
export const ASSET_STATUSES: AssetStatus[] = [
  'inService',
  'outOfService',
  'disposed',
  'fullyDepreciated',
  'pending',
  'underMaintenance',
  'reserved',
  'lostMissing',
  'leased',
];
export type AssetCategory = 'Computer Equipment' | 'Furniture & Fixtures' | 'Vehicles' | 'Machinery' | 'Buildings' | 'Land';
export type TransactionType = 'addition' | 'depreciation' | 'revaluation' | 'disposal' | 'transfer';
export type TransactionStatus = 'draft' | 'pending' | 'approved' | 'posted' | 'rejected';

export interface Asset {
  id: string;
  code: string;
  name: string;
  description?: string;
  category: AssetCategory;
  subCategory?: string;
  location: string;
  subLocation?: string;
  status: AssetStatus;
  
  // Financial
  acquisitionCost: number;
  netBookValue: number;
  accumulatedDepreciation: number;
  currency: string;
  acquisitionDate: string;
  depreciationMethod?: string;
  usefulLife?: number;
  residualValue?: number;
  quantity?: number;
  
  // Identification
  serialNumber?: string;
  manufacturer?: string;
  model?: string;
  barcode?: string;
  registrationNumber?: string;
  
  // Barcode/QR Code
  barcodeType?: 'barcode' | 'qrcode';
  barcodeValue?: string;
  
  // Assignment
  assignedTo?: string;
  assignedDate?: string;
  
  // Insurance & Warranty
  insurancePolicyNumber?: string;
  insuranceProvider?: string;
  insuranceStartDate?: string;
  insuranceExpiryDate?: string;
  insuranceValue?: number;
  insuranceNotes?: string;
  warrantyStartDate?: string;
  warrantyExpiryDate?: string;
  warrantyProvider?: string;
  warrantyCost?: number;
  warrantyTerms?: string;
  warrantyCertificationUrl?: string;
  warrantyNotes?: string;
  
  // Supplier
  supplierId?: string;
  supplierName?: string;
  purchaseOrderNumber?: string;
  invoiceReference?: string;
  hirePurchase?: boolean;
  
  // User Fields
  userValue1?: number; // Insurance Value
  userValue2?: number;
  userValue3?: number;
  userValue4?: number;
  userValue5?: number; // Residual Costs
  userKey1?: string;
  userKey2?: string;
  userKey3?: string;
  userKey4?: string;
  userKey5?: string;
  userDate1?: string;
  userDate2?: string;
  userDate3?: string;
  
  // Additional Fields
  resourceCode?: string;
  subLedger?: string;
  used?: boolean; // Indicates if asset has transactions
  priceRecordLink?: string; // Hidden field for HCSS price record lookup
  
  // Depreciation Settings
  depreciationPercentage?: number;
  depreciationYears?: number;
  postingFrequency?: string;
  inServicePeriod?: number;
  inServiceYear?: number;
  nextPostingPeriod?: number;
  nextPostingYear?: number;
  postingsMade?: number;
  totalPostings?: number;
  doNotDepreciate?: boolean;
  baseValueOn?: string;
  remainingPostings?: number;
  originalPostings?: number;
  currentBaseValue?: number;
  
  // Nominal Accounts
  assetCostNominal?: string;
  assetCostNominalName?: string;
  depreciationBSNominal?: string;
  depreciationBSNominalName?: string;
  depreciationPLNominal?: string;
  depreciationPLNominalName?: string;
  disposalsNominal?: string;
  disposalsNominalName?: string;
  revaluationBSNominal?: string;
  revaluationBSNominalName?: string;
  revaluationPLNominal?: string;
  revaluationPLNominalName?: string;
  disposalsBankAccountNominal?: string;
  disposalsBankAccountNominalName?: string;
  additionsWIPBankAccountNominal?: string;
  additionsWIPBankAccountNominalName?: string;
  
  // Costing
  project?: string;
  projectName?: string;
  costCentre?: string;
  costCentreName?: string;
  
  // Linked Assets
  linkedAssets?: string[];
  parentAssetId?: string;
  linkedEquipment?: EquipmentLink[];
  
  // Metadata
  photos?: AssetPhoto[];
  attachments?: Attachment[];
  notes?: string;
  createdBy: string;
  createdDate: string;
  modifiedBy?: string;
  modifiedDate?: string;
  tags?: string[];
  
  importJobId?: string;
  
  // Alert summary
  alertSummary?: AlertSummary;
}

export interface AssetPhoto {
  id: string;
  url: string;
  isPrimary: boolean;
  uploadedDate: string;
  fileName?: string;
  fileSize?: number;
}

export interface EquipmentLink {
  id: string;
  equipmentId: string;
  equipmentName: string;
  description?: string;
  linkedDate: string;
}

export interface SupplierAddress {
  id: string;
  type: 'billing' | 'shipping' | 'other';
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state?: string;
  postalCode: string;
  country: string;
}

export interface AssetYearValue {
  year: string;
  cost: {
    startOfYear: number;
    additions: number;
    disposals: number;
    revaluation: number;
    endOfYear: number;
  };
  depreciation: {
    startOfYear: number;
    charge: number;
    disposals: number;
    revaluation: number;
    endOfYear: number;
  };
  netBookValue: {
    startOfYear: number;
    endOfYear: number;
  };
}

export interface Attachment {
  id: string;
  name: string;
  type: string;
  size: number;
  uploadedBy: string;
  uploadedDate: string;
  url: string;
}

export interface Transaction {
  id: string;
  auditNumber?: number;
  transactionDate: string;
  type: TransactionType;
  assetId: string;
  assetName: string;
  amount: number;
  currency: string;
  batchId?: string;
  status: TransactionStatus;
  description?: string;
  items?: TransactionItem[];
  reference?: string;
  
  // Workflow
  preparedBy: string;
  preparedDate: string;
  approvedBy?: string;
  approvedDate?: string;
  postedBy?: string;
  postedDate?: string;
  
  // Journal
  journalReference?: string;
  debitAccount?: string;
  creditAccount?: string;
  
  notes?: string;
}

export interface BulkTransaction {
  id: string;
  batchId: string;
  type: TransactionType;
  status: TransactionStatus;
  totalAmount: number;
  currency: string;
  itemCount: number;
  items: TransactionItem[];
  
  // Workflow
  preparedBy: string;
  preparedDate: string;
  approvedBy?: string;
  approvedDate?: string;
  postedBy?: string;
  postedDate?: string;
  
  validationErrors?: ValidationError[];
  notes?: string;
}

export interface TransactionItem {
  id: string;
  assetId: string;
  assetName: string;
  amount: number;
  description?: string;
  validationStatus?: 'valid' | 'warning' | 'error';
  validationMessages?: string[];
}

export interface ValidationError {
  itemId: string;
  field: string;
  message: string;
  severity: 'error' | 'warning';
}

export interface KPI {
  label: string;
  value: string | number;
  change?: number;
  trend?: 'up' | 'down' | 'neutral';
  icon?: string;
}

export interface FilterOption {
  label: string;
  value: string;
  count?: number;
}

export interface SavedView {
  id: string;
  name: string;
  filters: Record<string, any>;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  columns?: string[];
}

export interface AuditEntry {
  id: string;
  timestamp: string;
  user: string;
  action: string;
  field?: string;
  oldValue?: string;
  newValue?: string;
  notes?: string;
  assetId?: string;
}
